import { t } from '@aquabox/shared';
import type { MessageKey } from '@aquabox/shared';
import { ChannelPill } from '../../components/Pills';
import { CHANNEL_COLOR } from '../../lib/labels';
import type { Dashboard, OrderChannel } from '../../api/types';

const CHANNEL_ORDER: OrderChannel[] = [
  'GETIR_SU',
  'TRENDYOL',
  'YEMEKSEPETI',
  'PHONE',
  'WHATSAPP',
  'BRAND_APP',
];

type SourceKey = 'ABANT_PANEL' | 'HAYAT_PANEL' | 'BIZIM';

const SOURCE_LABEL: Record<SourceKey, MessageKey> = {
  ABANT_PANEL: 'panelRep.sourceAbant',
  HAYAT_PANEL: 'panelRep.sourceHayat',
  BIZIM: 'panelRep.sourceBizim',
};

/**
 * "Kanal dağılımı" kartı (panel §3.3 C).
 *
 * Bugün: gerçek bugünkü sayım + bugünkü panel kaynak kırılımı. `today.bySource` panel
 * dahil TÜM kaynakları özetlediği için panel satırlarını burada KANAL bazında değil
 * KAYNAK bazında ikinci satır olarak gösteriyoruz: "Trendyol · Abant 142 · Hayat 17".
 *
 * Kanal başına ciro hâlâ sunucudan GELMİYOR; sayım kanal başına zaten yetiyor — bkz.
 * eski yorum.
 */
export function ChannelDistributionCard({
  byChannel,
  bySource,
}: {
  byChannel: Dashboard['today']['byChannel'];
  bySource: Dashboard['today']['bySource'];
}) {
  const countByChannel = new Map(byChannel.map((s) => [s.channel, s.count]));
  const maxCount = Math.max(1, ...CHANNEL_ORDER.map((s) => countByChannel.get(s) ?? 0));
  return (
    <div className="card card-pad">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <h2 className="section-title">{t('panel.channelDistribution')}</h2>
      </div>
      <ul style={{ listStyle: 'none' }}>
        {CHANNEL_ORDER.map((channel) => {
          const count = countByChannel.get(channel) ?? 0;
          const pct = (count / maxCount) * 100;
          return (
            <li
              key={channel}
              style={{
                display: 'grid',
                gridTemplateColumns: '130px 1fr 90px',
                alignItems: 'center',
                gap: 12,
                padding: '9px 0',
              }}
            >
              <ChannelPill channel={channel} />
              <span
                style={{
                  height: 12,
                  borderRadius: 4,
                  background: 'var(--page-bg)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${pct}%`,
                    background: CHANNEL_COLOR[channel],
                    borderRadius: 4,
                  }}
                />
              </span>
              <span style={{ textAlign: 'right', fontWeight: 500, fontSize: 13.5 }}>
                {t('overview.orderCount', { n: count })}
              </span>
            </li>
          );
        })}
      </ul>
      {/* Kaynak kırılımı — yalnız panel kaynakları (Bugün sekmesi). */}
      {bySource.length > 0 ? (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 14,
            flexWrap: 'wrap',
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('rep.colTotal')}:
          </span>
          {bySource.map((s) => (
            <span key={s.source}>
              <span style={{ color: 'var(--text-secondary)' }}>{t(SOURCE_LABEL[s.source])}</span>{' '}
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.orders}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
